/**
 * 智能客服悬浮坞（移植自 ai_jufeng_web ChatWidget.vue，Vue→React）。
 * 保留动画机器人吉祥物（SVG + 浮动/弹跳/摇摆/抖动/跳跃 循环 + 游走 + 眨眼/挥手 hover）+ 客服面板。
 * 对话接口走 /api/v2/chat/{welcome,chat}；服务器未就绪时回退本地知识库（改这里内容即可换话术）。
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Trash2, X, Send } from 'lucide-react';

import { ROBOT } from './robotColors';

interface ChatMsg {
  role: 'bot' | 'user';
  text: string;
  suggestions?: string[] | undefined;
  time: string;
}

const ANIM = ['sd-float', 'sd-bounce', 'sd-sway', 'sd-wiggle', 'sd-hop'] as const;
const QUICK = ['如何充值 / 购买积分？', '怎么新建项目？', '生成失败怎么办？', '如何导出数据？', '怎么联系人工客服？', '支持哪些模型？'];

function nowTime(): string {
  const d = new Date();
  return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
}

/** 本地知识库回退（服务器 /api/v2/chat 不可用时）。改这里即可换话术。 */
function localReply(text: string): { answer: string; suggestions: string[] } {
  const t = text.toLowerCase();
  if (t.includes('充值') || t.includes('积分') || t.includes('付款') || t.includes('购买')) {
    return { answer: '在左下角**用户信息**里点「充值」，或在 **AI 生成台** 顶部按积分预估购买套餐即可。积分用于文本/图像/视频/配音生成。', suggestions: ['有哪些套餐？', '积分怎么计费？'] };
  }
  if (t.includes('新建') || t.includes('项目') || t.includes('开始') || t.includes('创作')) {
    return { answer: '首页点「新建画布」或选一个**题材模板一键起片**；也可进**故事板**按向导从剧本到成片。', suggestions: ['模板都有哪些？', '故事板怎么用？'] };
  }
  if (t.includes('失败') || t.includes('报错') || t.includes('不出') || t.includes('错误')) {
    return { answer: '先刷新页面（Ctrl+F5）；确认积分充足；若仍失败，用右下角「问题反馈」提交截图，我们会尽快处理。', suggestions: ['怎么反馈问题？', '联系人工客服'] };
  }
  if (t.includes('导出') || t.includes('下载')) {
    return { answer: '在**剪辑**页可「导出剪映 / FCPXML」；生成结果卡上点下载图标导出图片/视频/音频。', suggestions: ['剪辑怎么用？'] };
  }
  if (t.includes('人工') || t.includes('客服') || t.includes('联系')) {
    return { answer: '可点右下角「问题反馈」提交，或邮件联系 support@aisom.cn。工作时间会尽快回复你～', suggestions: ['怎么反馈问题？'] };
  }
  if (t.includes('模型') || t.includes('支持')) {
    return { answer: '支持文本/图像/视频/语音等多模型（通义千问、通义万相、CosyVoice 等），在 AI 生成台可切换。', suggestions: ['有哪些套餐？'] };
  }
  return { answer: '你好！我是智创短剧创作平台的 AI 客服～ 关于充值、新建项目、生成、导出、故障等问题都可以问我，或点下方常见问题。', suggestions: QUICK.slice(0, 4) };
}

export function SupportDock() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMsg[]>([]);
  const [input, setInput] = useState('');
  const [typing, setTyping] = useState(false);
  const [anim, setAnim] = useState<string>('sd-float');
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // 机器人：循环切动画 + 游走
  useEffect(() => {
    let alive = true;
    const cycle = (): void => {
      if (!alive) return;
      setAnim(ANIM[Math.floor(Math.random() * ANIM.length)] ?? 'sd-float');
      setTimeout(cycle, 5000 + Math.random() * 3000);
    };
    const wander = (): void => {
      if (!alive) return;
      setOffset({ x: (Math.random() - 0.5) * 60, y: (Math.random() - 0.5) * 40 });
      setTimeout(wander, 4000 + Math.random() * 3000);
    };
    const t1 = setTimeout(cycle, 5000);
    const t2 = setTimeout(wander, 4000);
    return () => {
      alive = false;
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, []);

  const scrollBottom = useCallback(() => {
    requestAnimationFrame(() => {
      if (scrollRef.current !== null) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    });
  }, []);

  async function ask(text: string): Promise<void> {
    setTyping(true);
    scrollBottom();
    try {
      const resp = await fetch('/api/v2/chat/chat', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ message: text }) });
      const env = (await resp.json()) as { data?: { answer?: string; suggestions?: string[] } };
      const res = env.data ?? {};
      setMessages((m) => [...m, { role: 'bot', text: res.answer ?? localReply(text).answer, suggestions: res.suggestions, time: nowTime() }]);
    } catch {
      const r = localReply(text);
      setMessages((m) => [...m, { role: 'bot', text: r.answer, suggestions: r.suggestions, time: nowTime() }]);
    } finally {
      setTyping(false);
      scrollBottom();
    }
  }

  async function openChat(): Promise<void> {
    setOpen(true);
    if (messages.length === 0) {
      setTyping(true);
      try {
        const resp = await fetch('/api/v2/chat/welcome');
        const env = (await resp.json()) as { data?: { answer?: string; suggestions?: string[] } };
        const res = env.data ?? {};
        setMessages([{ role: 'bot', text: res.answer ?? '您好！我是智创短剧创作平台的 AI 智能客服，很高兴为您服务～ 关于充值、创作、生成、导出、故障等问题都可以问我。', suggestions: res.suggestions ?? QUICK.slice(0, 4), time: nowTime() }]);
      } catch {
        setMessages([{ role: 'bot', text: '您好！我是智创短剧创作平台的 AI 智能客服，很高兴为您服务～ 关于充值、创作、生成、导出、故障等问题都可以问我。', suggestions: QUICK.slice(0, 4), time: nowTime() }]);
      } finally {
        setTyping(false);
        scrollBottom();
      }
    }
    requestAnimationFrame(() => inputRef.current?.focus());
  }

  function send(): void {
    const text = input.trim();
    if (text === '' || typing) return;
    setMessages((m) => [...m, { role: 'user', text, time: nowTime() }]);
    setInput('');
    void ask(text);
  }
  function clearChat(): void {
    setMessages([]);
  }

  return (
    <div className="support-dock">
      {!open && (
        <button type="button" className="sd-fab" style={{ right: 24 + offset.x, bottom: 24 + offset.y }} onClick={() => void openChat()} aria-label="AI 智能客服">
          <div className={`sd-mascot ${anim}`}>
            <div className="sd-glow" />
            <div className="sd-particle sd-p1" /><div className="sd-particle sd-p2" /><div className="sd-particle sd-p3" />
            <svg className="sd-robot" viewBox="0 0 100 120" xmlns="http://www.w3.org/2000/svg">
              <line x1="50" y1="18" x2="50" y2="6" stroke="url(#sdAnt)" strokeWidth="2.5" strokeLinecap="round" />
              <circle cx="50" cy="4" r="3.5" fill={ROBOT.cyan} className="sd-antenna" />
              <rect x="22" y="18" width="56" height="42" rx="18" fill="url(#sdHead)" stroke="rgba(0,210,255,0.3)" strokeWidth="1" />
              <rect x="28" y="28" width="44" height="22" rx="11" fill="rgba(0,0,0,0.35)" stroke="rgba(0,210,255,0.15)" strokeWidth="0.5" />
              <circle cx="40" cy="39" r="5.5" fill={ROBOT.cyan} className="sd-eye sd-eye-l" />
              <circle cx="60" cy="39" r="5.5" fill={ROBOT.cyan} className="sd-eye sd-eye-r" />
              <circle cx="41.5" cy="37" r="2" fill="rgba(255,255,255,0.85)" className="sd-pupil sd-pupil-l" />
              <circle cx="61.5" cy="37" r="2" fill="rgba(255,255,255,0.85)" className="sd-pupil sd-pupil-r" />
              <rect x="42" y="52" width="16" height="3" rx="1.5" fill="rgba(0,210,255,0.5)" className="sd-mouth" />
              <rect x="30" y="64" width="40" height="30" rx="12" fill="url(#sdBody)" stroke="rgba(108,92,231,0.25)" strokeWidth="1" />
              <circle cx="50" cy="76" r="5" fill="url(#sdChest)" className="sd-chest" />
              <circle cx="50" cy="76" r="2.5" fill={ROBOT.cyan} opacity="0.8" className="sd-core" />
              <rect x="15" y="68" width="13" height="7" rx="3.5" fill="url(#sdArm)" className="sd-arm sd-arm-l" />
              <rect x="72" y="68" width="13" height="7" rx="3.5" fill="url(#sdArm)" className="sd-arm sd-arm-r" />
              <defs>
                <linearGradient id="sdHead" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor={ROBOT.purple} /><stop offset="100%" stopColor={ROBOT.purpleDeep} /></linearGradient>
                <linearGradient id="sdBody" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor={ROBOT.purpleMid} /><stop offset="100%" stopColor={ROBOT.purple} /></linearGradient>
                <linearGradient id="sdArm" x1="0%" y1="0%" x2="100%" y2="0%"><stop offset="0%" stopColor={ROBOT.purple} /><stop offset="100%" stopColor={ROBOT.cyan} /></linearGradient>
                <linearGradient id="sdAnt" x1="0%" y1="100%" x2="0%" y2="0%"><stop offset="0%" stopColor={ROBOT.purple} /><stop offset="100%" stopColor={ROBOT.cyan} /></linearGradient>
                <radialGradient id="sdChest"><stop offset="0%" stopColor="rgba(0,210,255,0.6)" /><stop offset="100%" stopColor="rgba(108,92,231,0.1)" /></radialGradient>
              </defs>
            </svg>
          </div>
          <span className="sd-fab-label">AI 智能客服</span>
        </button>
      )}

      {open && (
        <div className="sd-window">
          <div className="sd-header">
            <div className="sd-h-left">
              <span className="sd-avatar">🤖</span>
              <div>
                <div className="sd-name">AI 智能客服</div>
                <div className="sd-status"><span className="sd-dot" />在线 · 即时回复</div>
              </div>
            </div>
            <div className="sd-h-right">
              <button type="button" className="sd-hbtn" onClick={clearChat} aria-label="清空"><Trash2 size={15} /></button>
              <button type="button" className="sd-hbtn sd-close" onClick={() => setOpen(false)} aria-label="关闭"><X size={17} /></button>
            </div>
          </div>

          <div ref={scrollRef} className="sd-msgs">
            {messages.map((m, i) => (
              <div key={i} className={`sd-msg ${m.role}`}>
                {m.role === 'bot' && <span className="sd-m-avatar">🤖</span>}
                <div className="sd-bubble">
                  <div className="sd-content" dangerouslySetInnerHTML={{ __html: m.text.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>').replace(/\n/g, '<br>') }} />
                  {m.role === 'bot' && m.suggestions !== undefined && m.suggestions.length > 0 && (
                    <div className="sd-sugs">
                      {m.suggestions.map((s, j) => (<button key={j} type="button" className="sd-sug" onClick={() => void ask(s)}>{s}</button>))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {typing && <div className="sd-msg bot"><span className="sd-m-avatar">🤖</span><div className="sd-bubble sd-typing"><span /><span /><span /></div></div>}
          </div>

          {messages.length <= 1 && !typing && (
            <div className="sd-quick">
              <div className="sd-quick-title">💡 你可能想问：</div>
              <div className="sd-quick-grid">
                {QUICK.map((s) => (<button key={s} type="button" className="sd-qbtn" onClick={() => void ask(s)}>{s}</button>))}
              </div>
            </div>
          )}

          <div className="sd-input">
            <input ref={inputRef} value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') send(); }} placeholder={typing ? '正在思考…' : '输入您的问题…'} disabled={typing} className="sd-field" />
            <button type="button" className="sd-send" onClick={send} disabled={input.trim() === '' || typing} aria-label="发送"><Send size={18} /></button>
          </div>
        </div>
      )}
    </div>
  );
}
